/* eslint-disable @typescript-eslint/no-namespace */
import { ReportAttributes, ReportRelationships } from 'dashboard/models/report';
import { CountryCode } from 'lib/constants/locales';
import { ApiModel, ApiResource } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

export interface SourceField {
  name?: string;
  value?: string;
}

export interface Source {
  fields?: SourceField[];
  'country-codes'?: CountryCode[];
  types?: string[];
}

export interface Media {
  title?: string;
  url?: string;
  date?: string;
  snippet?: string;
}

export interface RelatedSource {
  id?: string;
  name?: string;
  akas?: string[];
  sources?: Source[];
  media?: Media[];
  'match-types'?: string[];
  birthdates?: string[];
}

export interface AdverseMediaIgnoreListEntry {
  'entity-id': string;
  'ignored-at': string;
  'run-history-completed-at': string;
  user: string;
  'ignore-reason'?: string;
  match: RelatedSource;
}

export interface IgnoreListEntity {
  [entityId: string]: AdverseMediaIgnoreListEntry;
}

export interface IgnoreListMedia {
  [entityId: string]: AdverseMediaIgnoreListEntry[];
}

export interface ReportAdverseMediaAttributes extends ReportAttributes {
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  term?: string;
  birthdate?: string;
  'birthdate-year'?: string;
  'country-code'?: CountryCode;
  'related-sources'?: RelatedSource[];
  'ignore-list-entity'?: IgnoreListEntity;
  'ignore-list-media'?: IgnoreListMedia;
}

export type ReportAdverseMedia = ApiResource<
  ReportAdverseMediaAttributes,
  ReportRelationships,
  ApiModel.ReportAdverseMedia
>;

// full List from v1 and v2 API
// aka_exact
// aka_fuzzy
// equivalent_aka
// equivalent_name
// name_exact
// name_fuzzy
// phonetic_name
// removed_clerical_mark
// removed_organisation_prefix
// removed_organisation_suffix
// removed_personal_suffix
// removed_personal_title
// unknown
// year_of_birth

// digit_to_word - new
// edit_distance - new
// exact_birth_year_match - new
// exact_match - new
// fuzzy_birth_year_match - new
// initial_to_word - new
// name_variations_removal - new
// phonetic - new
// phonetic_aka - new
// removed_spaces - new
// word_to_digit - new
// word_to_initial - new

export enum MatchLevel {
  AkaExact = 'aka_exact',
  AkaFuzzy = 'aka_fuzzy',
  EquivalentAka = 'equivalent_aka',
  EquivalentName = 'equivalent_name',
  NameExact = 'name_exact',
  NameFuzzy = 'name_fuzzy',
  PhoneticName = 'phonetic_name',
  // a clerical mark, for example 'DECEASED', was stripped from the search term
  RemovedClericalMark = 'removed_clerical_mark',
  // a personal suffix, for example 'PhD', was stripped from the search term
  RemovedPersonalSuffix = 'removed_personal_suffix',
  // a personal title, for example 'Mrs', was stripped from the search term
  RemovedPersonalTitle = 'removed_personal_title',
  // an organisation prefix, for example 'JSC', was stripped from the search term
  RemovedOrganizationPrefix = 'removed_organisation_prefix',
  // an organisation suffix, for example 'Ltd', was stripped from the search term
  RemovedOrganizationSuffix = 'removed_organisation_suffix',
  Unknown = 'unknown',
  YearOfBirth = 'year_of_birth',

  // TODO: (malav) maybe remove from below when CA fixes this bug
  DigitToWord = 'digit_to_word',
  EditDistance = 'edit_distance',
  ExactBirthYearMatch = 'exact_birth_year_match',
  ExactMatch = 'exact_match',
  FuzzyBirthYearMatch = 'fuzzy_birth_year_match',
  InitialToWord = 'initial_to_word',
  NameVariationsRemoval = 'name_variations_removal',
  Phonetic = 'phonetic',
  PhoneticAka = 'phonetic_aka',
  RemovedSpaces = 'removed_spaces',
  WordToDigit = 'word_to_digit',
  WordToInitial = 'word_to_initial',
}

export function matchLevelDisplayText(matchLevel: MatchLevel) {
  switch (matchLevel) {
    case MatchLevel.NameExact:
      return 'Matched the name exactly';
    case MatchLevel.AkaExact:
      return 'Matched against an AKA name exactly';
    case MatchLevel.NameFuzzy:
      return 'Matched closely to the name';
    case MatchLevel.AkaFuzzy:
      return 'Matched closely to an AKA name';
    case MatchLevel.PhoneticName:
      return 'Matched the name phonetically';
    case MatchLevel.EquivalentName:
      return 'Matched the name with a synonym';
    case MatchLevel.EquivalentAka:
      return 'Matched an AKA name with a synonym';
    case MatchLevel.Unknown:
      return 'Matched for a complex reason, such as acronym';
    case MatchLevel.YearOfBirth:
      return 'Approximately matched search birth year';
    case MatchLevel.RemovedClericalMark:
      return 'Clerical marks removed from search input';
    case MatchLevel.RemovedPersonalSuffix:
      return 'Personal suffixes removed from search input';
    case MatchLevel.RemovedPersonalTitle:
      return 'Personal titles removed from search input';
    case MatchLevel.RemovedOrganizationPrefix:
      return 'Organization prefixes removed from search input';
    case MatchLevel.RemovedOrganizationSuffix:
      return 'Organization suffixes removed from search input';
    // disregard as these are from V1 API and should not show up but they are because of a CA bug
    case MatchLevel.DigitToWord:
    case MatchLevel.EditDistance:
    case MatchLevel.ExactBirthYearMatch:
    case MatchLevel.ExactMatch:
    case MatchLevel.FuzzyBirthYearMatch:
    case MatchLevel.InitialToWord:
    case MatchLevel.NameVariationsRemoval:
    case MatchLevel.Phonetic:
    case MatchLevel.PhoneticAka:
    case MatchLevel.RemovedSpaces:
    case MatchLevel.WordToDigit:
    case MatchLevel.WordToInitial:
      return '';
    default:
      assertNever(matchLevel);
  }
}

export function matchLevelShortDisplayText(matchLevel: MatchLevel) {
  switch (matchLevel) {
    case MatchLevel.NameExact:
      return 'Exact match';
    case MatchLevel.AkaExact:
      return 'AKA match';
    case MatchLevel.NameFuzzy:
      return 'Close match';
    case MatchLevel.AkaFuzzy:
      return 'Close AKA match';
    case MatchLevel.PhoneticName:
      return 'Phonetic match';
    case MatchLevel.EquivalentName:
      return 'Equivalent match';
    case MatchLevel.EquivalentAka:
      return 'Equivalent AKA match';
    case MatchLevel.Unknown:
      return 'Complex match';
    // Don't display additional match level type info to save screen space
    case MatchLevel.YearOfBirth:
    case MatchLevel.RemovedClericalMark:
    case MatchLevel.RemovedPersonalSuffix:
    case MatchLevel.RemovedPersonalTitle:
    case MatchLevel.RemovedOrganizationPrefix:
    case MatchLevel.RemovedOrganizationSuffix:
      return '';
    // disregard as these are from V1 API and should not show up but they are because of a CA bug
    case MatchLevel.DigitToWord:
    case MatchLevel.EditDistance:
    case MatchLevel.ExactBirthYearMatch:
    case MatchLevel.ExactMatch:
    case MatchLevel.FuzzyBirthYearMatch:
    case MatchLevel.InitialToWord:
    case MatchLevel.NameVariationsRemoval:
    case MatchLevel.Phonetic:
    case MatchLevel.PhoneticAka:
    case MatchLevel.RemovedSpaces:
    case MatchLevel.WordToDigit:
    case MatchLevel.WordToInitial:
      return '';
    default:
      assertNever(matchLevel);
  }
}

export enum AdverseMediaTypeV1 {
  FinancialCrime = 'adverse-media-financial-crime',
  ViolentCrime = 'adverse-media-violent-crime',
  SexualCrime = 'adverse-media-sexual-crime',
  Terrorism = 'adverse-media-terrorism',
  Fraud = 'adverse-media-fraud',
  Narcotics = 'adverse-media-narcotics',
  General = 'adverse-media-general',
}

export enum AdverseMediaTypeV2 {
  V2Property = 'adverse-media-v2-property',
  V2FinancialAMLCFT = 'adverse-media-v2-financial-aml-cft',
  V2FraudLinked = 'adverse-media-v2-fraud-linked',
  V2NarcoticsAMLCFT = 'adverse-media-v2-narcotics-aml-cft',
  V2ViolenceAMLCFT = 'adverse-media-v2-violence-aml-cft',
  V2Terrorism = 'adverse-media-v2-terrorism',
  V2Cybercrime = 'adverse-media-v2-cybercrime',
  V2GeneralAMLCFT = 'adverse-media-v2-general-aml-cft',
  V2Regulatory = 'adverse-media-v2-regulatory',
  V2FinancialDifficulty = 'adverse-media-v2-financial-difficulty',
  V2ViolenceNonAMLCFT = 'adverse-media-v2-violence-non-aml-cft',
  V2OtherFinancial = 'adverse-media-v2-other-financial',
  V2OtherSerious = 'adverse-media-v2-other-serious',
  V2OtherMinor = 'adverse-media-v2-other-minor',
}

export type AdverseMediaType = AdverseMediaTypeV1 | AdverseMediaTypeV2;

export function adverseMediaTypeDisplayText(type: AdverseMediaType) {
  switch (type) {
    case AdverseMediaTypeV1.FinancialCrime:
      return 'Financial crime';
    case AdverseMediaTypeV1.ViolentCrime:
      return 'Violent crime';
    case AdverseMediaTypeV1.SexualCrime:
      return 'Sexual crime';
    case AdverseMediaTypeV1.Terrorism:
      return 'Terrorism';
    case AdverseMediaTypeV1.Fraud:
      return 'Fraud';
    case AdverseMediaTypeV1.Narcotics:
      return 'Narcotics';
    case AdverseMediaTypeV1.General:
      return 'General';

    case AdverseMediaTypeV2.V2Property:
      return 'Property';
    case AdverseMediaTypeV2.V2FinancialAMLCFT:
      return 'Financial AML/CFT';
    case AdverseMediaTypeV2.V2FraudLinked:
      return 'Fraud-linked';
    case AdverseMediaTypeV2.V2NarcoticsAMLCFT:
      return 'Narcotics AML/CFT';
    case AdverseMediaTypeV2.V2ViolenceAMLCFT:
      return 'Violence AML/CFT';
    case AdverseMediaTypeV2.V2Terrorism:
      return 'Terrorism';
    case AdverseMediaTypeV2.V2Cybercrime:
      return 'Cybercrime';
    case AdverseMediaTypeV2.V2GeneralAMLCFT:
      return 'General AML/CFT';
    case AdverseMediaTypeV2.V2Regulatory:
      return 'Regulatory';
    case AdverseMediaTypeV2.V2FinancialDifficulty:
      return 'Financial difficulty';
    case AdverseMediaTypeV2.V2ViolenceNonAMLCFT:
      return 'Violence NON-AML/CFT';
    case AdverseMediaTypeV2.V2OtherFinancial:
      return 'Other Financial';
    case AdverseMediaTypeV2.V2OtherSerious:
      return 'Other Serious';
    case AdverseMediaTypeV2.V2OtherMinor:
      return 'Other Minor';

    default:
      assertNever(type);
  }
}

export function adverseMediaTypeTooltipText(type: AdverseMediaType) {
  switch (type) {
    case AdverseMediaTypeV1.FinancialCrime:
    case AdverseMediaTypeV1.ViolentCrime:
    case AdverseMediaTypeV1.SexualCrime:
    case AdverseMediaTypeV1.Terrorism:
    case AdverseMediaTypeV1.Fraud:
    case AdverseMediaTypeV1.Narcotics:
    case AdverseMediaTypeV1.General:
      return null;
    case AdverseMediaTypeV2.V2Property:
      return (
        'Topics involving the unlawful taking of property, including money and intellectual property (piracy ' +
        '/ copyright infringement). Includes theft, burglary, and possession of stolen property. Also includes ' +
        'embezzlement, misappropriation, larceny.'
      );
    case AdverseMediaTypeV2.V2FinancialAMLCFT:
      return (
        'Financial topics related to AML/CFT, that are not explicitly property crime. Includes money ' +
        'laundering, tax related offences, and insider trading.'
      );
    case AdverseMediaTypeV2.V2FraudLinked:
      return (
        'Fraud is intentional deception to secure unfair or unlawful gain, or to deprive a victim of a legal ' +
        'right. Specific topics include fraud (corporate, VAT, identity, etc.), scams, counterfeiting, and ' +
        'forgery.'
      );
    case AdverseMediaTypeV2.V2NarcoticsAMLCFT:
      return (
        'All producing, trafficking, distribution, and dealing of narcotics (drugs) - will be linked to AML as ' +
        'a predicate offence. Does not include drug usage or possession.'
      );
    case AdverseMediaTypeV2.V2ViolenceAMLCFT:
      return (
        'Serious violence related topics that are relevant to financial institutions. Primarily focused on FATF ' +
        'and EU directive predicate offences. Excludes non predicate offences (assault, most sexual crimes) but ' +
        'does include murder and grievous kidnapping; human trafficking; child pornography; illegal arms / ' +
        'weapons possession / trading; pimping; slavery.'
      );
    case AdverseMediaTypeV2.V2Terrorism:
      return (
        'All terrorism related topics including attacks, financing, membership, and aiding. Includes all forms ' +
        'of terrorism - religious, political (left & right wing), state.'
      );
    case AdverseMediaTypeV2.V2Cybercrime:
      return (
        'Access to information systems; system interference; data interference; interception. Does not include ' +
        'topics only because computers are involved (such as fraud), but only topics where it is specifically ' +
        'computer/cyber related.'
      );
    case AdverseMediaTypeV2.V2GeneralAMLCFT:
      return (
        'Smuggling (goods & migrants); organised crime and gang activity; corruption and bribery; environmental ' +
        'crimes including poaching and polluting; potential sanctions violations, risks, or proximity ' +
        'to sanctioned entities.'
      );
    case AdverseMediaTypeV2.V2Regulatory:
      return (
        'Topics that relate to activity that contravenes regulation - often this is specific to a particular ' +
        'country or state or Fines or disciplinary action by regulators and industry bodies; collusion / ' +
        'anti-trust / price-fixing issues; specific entities that have been denied by industry bodies and ' +
        'regulators; specific industry related issues such as front-running, greenmail, and churning for the ' +
        'stock market, or illegal gambling.'
      );
    case AdverseMediaTypeV2.V2FinancialDifficulty:
      return (
        'Topics where the subject is mentioned in the context of bankruptcy, having a high amount of debt, or ' +
        'experiencing fatal business issues. Media may include stories about store closures, lay offs, late ' +
        'payment to creditors, or the unexpected departure of a CFO.'
      );
    case AdverseMediaTypeV2.V2ViolenceNonAMLCFT:
      return (
        'Violence related topics that are not predicate offences for AML/CFT - everything violence related that ' +
        'is not specified in the "Violence AML/CFT". Includes assault, domestic violence & physical abuse, ' +
        'harassment, rape & sex offences. Also includes some state level topics such as massacres, human rights ' +
        'abuses, torture, and genocide.'
      );
    case AdverseMediaTypeV2.V2OtherFinancial:
      return (
        'Non AML/CFT related financial topics such as predatory lending, questionable billing practices, ' +
        'creative accounting, loan sharking, or tax avoidance.'
      );
    case AdverseMediaTypeV2.V2OtherSerious:
      return (
        'Potentially serious topics that are not AML/CFT related. Many different topics included such as ' +
        'perjury, arson, obstruction of justice, election interference, escaped fugitives, spying & espionage, ' +
        'treason, and civil disorder. Also includes non-specific crimes where very serious penalties are ' +
        'involved (e.g. death / life sentences).'
      );
    case AdverseMediaTypeV2.V2OtherMinor:
      return (
        'Other minor topics and difficult to classify topics that do not fall into any of the other classes. ' +
        'Includes things like drugs posession, prostitution, misconduct, conspiracy to commit a crime where the ' +
        'crime is not specified, vandalism, drink driving / DUI (driving under the influence), and commercial ' +
        'litigation between entities. May also include some more general corporate related topics like human ' +
        'resources issues / labour disputes, and health & safety.'
      );

    default:
      assertNever(type);
  }
}
